import { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare, Smartphone } from "lucide-react";

/**
 * InstallApp — small floating button that becomes an installer on Android
 * Chrome / Edge (using `beforeinstallprompt`) and an iOS instructions sheet on
 * Safari. Auto-hides when the app is already running standalone.
 */
const STORAGE_KEY = "psc_install_dismissed_at";
const DISMISS_HOURS = 24 * 7;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

const wasRecentlyDismissed = () => {
  const ts = Number(localStorage.getItem(STORAGE_KEY) || 0);
  return ts && Date.now() - ts < DISMISS_HOURS * 60 * 60 * 1000;
};

const InstallApp = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;

    const showWith = (e) => {
      if (e) setDeferred(e);
      setShow(true);
    };

    // The prompt event is captured in index.html before React mounts and
    // stashed on window — pick it up if it already fired.
    if (window.__pwaInstall && window.__pwaInstall.event) showWith(window.__pwaInstall.event);

    const onAvail = () => {
      if (window.__pwaInstall && window.__pwaInstall.event) showWith(window.__pwaInstall.event);
    };
    const bip = (e) => { e.preventDefault(); showWith(e); };
    const onInstalled = () => { setShow(false); setDeferred(null); };

    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("beforeinstallprompt", bip);
    window.addEventListener("appinstalled", onInstalled);

    if (isIOS()) setShow(true);

    return () => {
      window.removeEventListener("pwa-installable", onAvail);
      window.removeEventListener("beforeinstallprompt", bip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!show) return null;

  const onInstall = async () => {
    const evt = (window.__pwaInstall && window.__pwaInstall.event) || deferred;
    if (evt) {
      evt.prompt();
      const choice = await evt.userChoice;
      if (window.__pwaInstall) window.__pwaInstall.event = null;
      if (choice.outcome === "accepted") setShow(false);
      setDeferred(null);
    } else if (isIOS()) {
      setIosOpen(true);
    }
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setShow(false);
  };

  return (
    <>
      <div className="fixed bottom-4 right-4 z-30 max-w-xs sm:max-w-sm" data-testid="install-banner">
        <div className="bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] shadow-[6px_6px_0_rgba(20,184,166,0.25)] p-4 flex items-start gap-3">
          <div className="w-10 h-10 bg-teal-500 flex items-center justify-center flex-shrink-0">
            <Smartphone size={20} strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-teal-300">Install on your phone</div>
            <div className="text-sm font-bold mt-0.5 leading-snug">Use Compliance Hub like a native app</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={onInstall}
                data-testid="install-button"
                className="h-9 px-3 inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-400 text-white text-xs font-bold uppercase tracking-wide"
              >
                <Download size={14} strokeWidth={2.5}/>Install
              </button>
              <button
                onClick={dismiss}
                data-testid="install-dismiss"
                className="h-9 px-3 inline-flex items-center gap-1.5 bg-transparent border-2 border-white/30 hover:border-white text-white text-xs font-bold uppercase tracking-wide"
              >
                Not now
              </button>
            </div>
          </div>
          <button onClick={dismiss} aria-label="Dismiss" className="text-white/60 hover:text-white" data-testid="install-close">
            <X size={16} strokeWidth={2.5}/>
          </button>
        </div>
      </div>

      {iosOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setIosOpen(false)}
          data-testid="ios-instructions-overlay"
        >
          <div
            className="bg-white border-2 border-slate-900 w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="ios-instructions"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b-2 border-slate-200">
              <h3 className="font-black text-slate-900">Install on iPhone / iPad</h3>
              <button onClick={() => setIosOpen(false)} className="w-9 h-9 border-2 border-slate-300 flex items-center justify-center hover:bg-slate-100"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4 text-sm text-slate-700">
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 bg-[#1E1B4B] text-white font-bold flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <span>Tap the <strong>Share</strong> button at the bottom of Safari.
                    <span className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 bg-slate-100 border border-slate-200 align-middle"><Share size={12}/> Share</span></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 bg-[#1E1B4B] text-white font-bold flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <span>Scroll down and tap <strong>Add to Home Screen</strong>.
                    <span className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 bg-slate-100 border border-slate-200 align-middle"><PlusSquare size={12}/> Add to Home Screen</span></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 bg-[#1E1B4B] text-white font-bold flex items-center justify-center text-xs flex-shrink-0">3</span>
                  <span>Tap <strong>Add</strong> in the top-right. The Compliance Hub icon will appear on your home screen.</span>
                </li>
              </ol>
              <p className="text-xs text-slate-500 border-t border-slate-200 pt-4">Make sure you&apos;re using <strong>Safari</strong> (not Chrome) on iPhone — Apple only allows installation from Safari.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallApp;
